import { useMemo } from 'react';
import type { LeaseApplication } from '../types/lease-application';
import type { ApplicationFilter } from '../components/LeaseApplications/LeaseApplicationsTable';
import type { SortConfig } from '../types/lease';
import type { ApplicationSortField } from '../components/LeaseApplications/LeaseApplicationSortControls';

export function useFilteredAndSortedApplications(
  applications: LeaseApplication[] | undefined,
  filters: ApplicationFilter,
  sortConfig: SortConfig,
  searchQuery: string
) {
  return useMemo(() => {
    if (!applications) return [];

    let filteredApplications = applications;

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredApplications = filteredApplications.filter(app => 
        (app.app_lot?.toLowerCase() || '').includes(query) ||
        (app.app_type?.toLowerCase() || '').includes(query) ||
        (app.app_desc?.toLowerCase() || '').includes(query)
      );
    }

    // Apply filters
    if (filters.status) {
      filteredApplications = filteredApplications.filter(app => {
        if (filters.status === 'approved') return !!app.app_approv_date;
        if (filters.status === 'rejected') return app.app_final === 'rejected';
        return !app.app_approv_date && app.app_final !== 'rejected';
      });
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filteredApplications = filteredApplications.filter(app => 
        app.app_date ? new Date(app.app_date) >= fromDate : false
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filteredApplications = filteredApplications.filter(app => 
        app.app_date ? new Date(app.app_date) <= toDate : false
      );
    }

    // Apply sorting
    const sortedApplications = [...filteredApplications].sort((a, b) => {
      const field = sortConfig.field as ApplicationSortField;
      const direction = sortConfig.direction === 'asc' ? 1 : -1;

      switch (field) {
        case 'app_id':
          return direction * ((a.app_id || 0) - (b.app_id || 0));
        case 'app_lot':
          return direction * ((a.app_lot || '').localeCompare(b.app_lot || ''));
        case 'app_type':
          return direction * ((a.app_type || '').localeCompare(b.app_type || ''));
        case 'app_date':
          if (!a.app_date) return direction;
          if (!b.app_date) return -direction;
          return direction * (
            new Date(a.app_date).getTime() - new Date(b.app_date).getTime()
          );
        case 'app_approv_date':
          if (!a.app_approv_date) return direction;
          if (!b.app_approv_date) return -direction;
          return direction * (
            new Date(a.app_approv_date).getTime() - new Date(b.app_approv_date).getTime()
          );
        case 'status':
          const getStatusPriority = (app: LeaseApplication) => {
            if (app.app_approv_date) return 2; // Approved
            if (app.app_final === 'rejected') return 0; // Rejected
            return 1; // Pending
          };
          return direction * (getStatusPriority(a) - getStatusPriority(b));
        default:
          return 0;
      }
    });

    return sortedApplications;
  }, [applications, filters, sortConfig, searchQuery]);
}
